import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { CalendarIcon, UnmutedCameraIcon, UnmutedMicrophoneIcon, } from '@streamr/tv-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { Button } from '../components/Button';
import { LobbyFrame } from '../components/frames/LobbyFrame';
import { SessionFrame, SessionFrameList, } from '../components/frames/SessionFrame';
import { InAppBrowserGate } from '../components/InAppBrowserGate';
import { CenteredContentContainer } from '../components/layout/content/CenteredContentContainer';
import { FooterWithLeftLogo } from '../components/layout/footers/FooterWithLeftLogo';
import { HeaderWithLeftLogoAndControls } from '../components/layout/headers/HeaderWithLeftLogoAndControls';
import { PageLayout } from '../components/layout/PageLayout';
import { scheduleSession } from '../components/popups/ScheduleSessionPopup';
import { showSettingsPopup } from '../components/popups/SettingsPopup';
import { WorkspaceControls } from '../components/shared/WorkspaceControls';
import { CALL_MODE_ENABLED } from '../consts';
import { ScheduleService } from '../services/schedule/ScheduleService';
import { StreamrAppService } from '../services/streamrapp/StreamrAppService';
import { TimeService } from '../services/time/TimeService';
import { trackPageView } from '../utils/matomo';
import { formBroadcastPath } from '../utils/url';
// Track when the script is loaded in production
// this emulates the behavior of embedding the tracking
// script in the HTML.
trackPageView();
function useThrottledTime(intervalInMillis = 5000) {
    const now = TimeService.getOutput().useTime();
    const [throttled, setThrottled] = useState(() => now ?? Date.now());
    useEffect(() => {
        if (now !== undefined && now - throttled >= intervalInMillis) {
            setThrottled(now);
        }
    }, [now, throttled, intervalInMillis]);
    return throttled;
}
const HeaderActionButtons = ({ onActivateSessionClick, }) => {
    return (_jsxs(HeaderButtons, { children: [_jsx(SchedulePillButton, { type: "button", kind: "secondary", size: "mini", shape: "pill", icon: _jsx(CalendarIcon, {}), onClick: () => {
                    scheduleSession();
                } }), _jsx(ScheduleRegularButton, { type: "button", kind: "secondary", size: "mini", icon: _jsx(CalendarIcon, {}), onClick: () => {
                    scheduleSession();
                }, children: "Schedule" }), CALL_MODE_ENABLED && (_jsxs(_Fragment, { children: [_jsx(PillHeaderButton, { type: "button", size: "mini", shape: "pill", icon: _jsx(UnmutedMicrophoneIcon, { fill: "#f65f0a" }), onClick: () => {
                            onActivateSessionClick(undefined, true, 'call');
                        } }), _jsx(RegularHeaderButton, { type: "button", size: "mini", icon: _jsx(UnmutedMicrophoneIcon, { fill: "#f65f0a" }), onClick: () => {
                            onActivateSessionClick(undefined, true, 'call');
                        }, children: "Start Call" })] })), _jsx(PillHeaderButton, { type: "button", size: "mini", shape: "pill", icon: _jsx(UnmutedCameraIcon, { fill: "#f65f0a" }), onClick: () => {
                    onActivateSessionClick(undefined, true, 'broadcast');
                } }), _jsx(RegularHeaderButton, { type: "button", size: "mini", icon: _jsx(UnmutedCameraIcon, { fill: "#f65f0a" }), onClick: () => {
                    onActivateSessionClick(undefined, true, 'broadcast');
                }, children: "Start Broadcast" })] }));
};
export function LandingPage() {
    return (_jsx(InAppBrowserGate, { children: _jsx(LandingPageContent, {}) }));
}
function LandingPageContent() {
    const sessions = ScheduleService.getOutput().useScheduledSessions();
    const time = useThrottledTime(30000);
    const [pastSessions, upcomingSessions] = useMemo(() => {
        const past = [];
        const upcoming = [];
        for (const session of sessions) {
            (session.startsAt < time ? past : upcoming).push(session);
        }
        past.reverse();
        return [past, upcoming];
    }, [sessions, time]);
    const navigate = useNavigate();
    const activateSession = useCallback((savedSession, navigateToBroadcastPage, mode = 'broadcast') => {
        StreamrAppService.getNotificationInterface().notifyActivateSession(savedSession, mode, {
            onActivate: navigateToBroadcastPage
                ? (path) => void navigate(path)
                : undefined,
        });
    }, [navigate]);
    return (_jsx(PageLayout, { header: _jsx(HeaderWithLeftLogoAndControls, { topRowControls: _jsx(WorkspaceControls, { onKnobsClick: (anchorRef) => {
                    // biome-ignore lint/nursery/noFloatingPromises: TODO
                    showSettingsPopup({
                        anchorRef,
                    });
                } }), controls: sessions.length > 0 ? (_jsx(HeaderActionButtons, { onActivateSessionClick: activateSession })) : undefined }), footer: _jsx(FooterWithLeftLogo, {}), children: _jsxs(CenteredContentContainer, { children: [sessions.length === 0 && (_jsx(LobbyFrame, { onScheduleClick: () => {
                        scheduleSession();
                    }, onStartClick: () => {
                        activateSession(undefined, true, 'broadcast');
                    }, startButtonLabel: "Start Broadcast", onStartCallClick: CALL_MODE_ENABLED
                        ? () => {
                            activateSession(undefined, true, 'call');
                        }
                        : undefined })), upcomingSessions.length > 0 && (_jsx(SessionFrameList, { multi: sessions.length > 1, title: "Upcoming", children: upcomingSessions.map((session) => (_jsx(SessionFrame, { session: session, onActivateSessionClick: () => {
                            activateSession(session, true);
                        } }, session.id))) })), pastSessions.length > 0 && (_jsx(SessionFrameList, { multi: sessions.length > 1, title: upcomingSessions.length > 0 ? 'Past' : undefined, children: pastSessions.map((session) => (_jsx(SessionFrame, { session: session, onActivateSessionClick: () => {
                            activateSession(session, true);
                        }, onRecordingsClick: () => {
                            activateSession(session, false);
                            void navigate(formBroadcastPath(session, '/recordings'));
                        } }, session.id))) }))] }) }));
}
const PillHeaderButton = styled(Button) `
    padding: 0;
    width: 2.75rem;
    min-width: 2.75rem;
`;
const RegularHeaderButton = styled(Button) ``;
const SchedulePillButton = styled(PillHeaderButton) `
    &[class] {
        background-color: #4a4a4a;
    }
`;
const ScheduleRegularButton = styled(RegularHeaderButton) `
    &[class] {
        background-color: #4a4a4a;
    }
`;
const HeaderButtons = styled.div `
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    button {
        height: 2.75rem;
    }

    ${PillHeaderButton}, ${SchedulePillButton} {
        display: none;
    }

    @media (min-width: 37.5rem) {
        flex-direction: row;
        gap: 1rem;
    }
`;
